import { useLocale, useT } from '../i18n/locale-context';
import { tendersWord } from '../lib/format';
import type { CompanyPayoff } from '../lib/auth-client';

/**
 * The one renderer for what an ИНН submission found.
 *
 * Two surfaces show this — the settings/modal card and the onboarding wizard's
 * ИНН step — and they each had their own copy of the same four-branch switch.
 * Predictably, they drifted: adding the `{{word}}` interpolation that fixes
 * Russian's plural agreement («2 тендера», not «2 тендеров») updated one caller
 * and left the other rendering the literal text `{{word}}` on screen to real
 * users.
 *
 * A shared component is the fix, not a discipline problem. Any future change to
 * the payoff — a new branch, another interpolation — now lands in both places
 * by construction.
 */
export function CompanyPayoffLine({ payoff }: { payoff: CompanyPayoff }) {
  const t = useT();
  const { locale } = useLocale();

  // `none` deliberately reports no count. A branch that resolves to zero falls
  // through to the next one, and when nothing resolves at all, silence beats
  // telling somebody we found 0 about them.
  if (payoff.kind === 'none') return <>{t('company.saved')}</>;

  const word = tendersWord(payoff.count, locale);
  const key =
    payoff.kind === 'wins'
      ? 'company.foundWins'
      : payoff.kind === 'sector'
        ? 'company.foundSector'
        : 'company.foundRegion';

  return <>{t(key, { n: payoff.count, word })}</>;
}
